import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { Paywall } from "../components/Paywall";
import { QrCard } from "../components/QrCard";
import { StatCards } from "../components/StatCards";
import { FeedbackFeed } from "../components/FeedbackFeed";
import { AddLocationDialog } from "../components/AddLocationDialog";
import { LocationsOverview } from "../components/LocationsOverview";
import { SubscriptionPanel } from "../components/SubscriptionPanel";
import { AlertSettingsDialog } from "../components/AlertSettingsDialog";
import { SurveyEditorDialog } from "../components/SurveyEditorDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fmtDay, onTrial } from "../lib/api";

const Feedback = ({ stats, items, multi, locations, onToggle, onContacted }) => (
  <div className="space-y-8 min-w-0">
    <StatCards stats={stats} />
    <div>
      <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight mb-4">Customer feedback</h2>
      <FeedbackFeed items={items} showBusiness={multi} businesses={multi ? locations : undefined} onToggleAddressed={onToggle} onContacted={onContacted} />
    </div>
  </div>
);

const BillingList = ({ locations, onChange }) => {
  const subs = locations.filter((l) => l.subscription_status === "active");
  return (
    <section className="space-y-3" data-testid="billing-section">
      <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">Manage subscriptions</h2>
      {subs.length ? subs.map((l) => <SubscriptionPanel key={l.id} loc={l} onChange={onChange} compact />)
        : <p className="text-sm text-[#778580]">No active subscriptions yet.</p>}
    </section>
  );
};

const LocationView = ({ loc, items, onChange, onToggle, onContacted, multi }) =>
  loc.subscription_status !== "active" ? <Paywall location={loc} /> : (
    <div className="grid lg:grid-cols-[320px_1fr] gap-6 lg:gap-8 items-start">
      {onTrial(loc) && !loc.cancel_at_period_end && (
        <div className="lg:col-span-2 rounded-2xl bg-[#E3EEF3] text-[#2E6F8E] px-5 py-3 text-sm font-semibold" data-testid="trial-banner">
          Free trial active — ends {fmtDay(loc.trial_ends_at)}. Your card will then be charged $29.99/month.
        </div>
      )}
      <div className="lg:col-span-2 grid md:grid-cols-2 2xl:grid-cols-3 gap-4">
        <SubscriptionPanel loc={loc} onChange={onChange} />
        <AlertSettingsDialog loc={loc} multi={multi} onSaved={onChange} />
        <SurveyEditorDialog loc={loc} multi={multi} onSaved={onChange} />
      </div>
      <div className="lg:sticky lg:top-24"><QrCard business={loc} /></div>
      <Feedback stats={loc.stats} items={items} onToggle={onToggle} onContacted={onContacted} />
    </div>
  );

export default function Dashboard() {
  const { refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get("/business/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="min-h-screen bg-[#EEF1ED]"><AppHeader /><div className="py-24 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#2F5D4A]" /></div></div>;

  const locations = data.businesses;
  const billing = params.get("view") === "billing";
  const sel = params.get("loc") || (locations.length === 1 && !billing ? locations[0].id : "all");
  const current = locations.find((l) => l.id === sel);
  const select = (id) => setParams(id === "all" ? {} : { loc: id });
  const activeLocs = locations.filter((l) => l.subscription_status === "active");
  const onCreated = async (loc) => { await load(); refresh(); select(loc.id); };
  const onContacted = (id, contact) =>
    setData((d) => ({ ...d, feedback: d.feedback.map((f) => (f.id === id ? { ...f, contacts: [...(f.contacts || []), contact] } : f)) }));
  const toggleAddressed = async (id, addressed) => {
    try {
      await api.patch(`/business/feedback/${id}`, { addressed });
      setData((d) => ({ ...d, feedback: d.feedback.map((f) => (f.id === id ? { ...f, addressed } : f)) }));
      load();
      toast.success(addressed ? "Marked as read / addressed" : "Marked as unaddressed");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#EEF1ED]">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div className="min-w-0">
            <p className="label-caps">{current ? current.category || "Location" : `${locations.length} location${locations.length === 1 ? "" : "s"}`}</p>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="dashboard-business-title">
              {billing ? "Billing & subscriptions" : current ? current.label : "All locations"}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Select value={current && !billing ? sel : "all"} onValueChange={select}>
              <SelectTrigger data-testid="location-switcher" className="flex-1 sm:w-64 rounded-xl bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="location-option-all">All locations</SelectItem>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id} data-testid={`location-option-${l.slug}`}>
                    {l.label}{l.subscription_status !== "active" ? " (not active)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <AddLocationDialog defaults={locations[0]} onCreated={onCreated} />
          </div>
        </div>
        {billing ? (
          <div className="space-y-6 max-w-3xl">
            <BillingList locations={locations} onChange={load} />
          </div>
        ) : current ? (
          <LocationView loc={current} items={data.feedback.filter((f) => f.business_id === current.id)} onChange={load} onToggle={toggleAddressed} onContacted={onContacted} multi={locations.length > 1} />
        ) : (
          <div className="space-y-10">
            <LocationsOverview locations={locations} onSelect={select} />
            {activeLocs.length > 0 && <BillingList locations={locations} onChange={load} />}
            {activeLocs.length > 0 && <Feedback stats={data.stats} items={data.feedback} multi locations={activeLocs} onToggle={toggleAddressed} onContacted={onContacted} />}
          </div>
        )}
      </main>
    </div>
  );
}
